<template>
  <div
    id="header"
    class="text-xs sm:text-base px-0 sm:px-12 flex-end pl-12"
    :class="{ 'has-overlay-open': showChatPanel || showMobileMenu }"
    :style="`background-color: ${adminColor};`"
  >
    <div class="app-version">{{ appVersion }}-105...</div>
    <div class="left-menu-header hidden md:flex">
      <a @click="openLabelPrinter" class="label-printer-btn">
        {{ $t("message.header.label_printer") }}
      </a>
    </div>
    <BaseModal class="small" v-model="showStoreSelectionModal">
      <BaseStoreSelector @selected="showStoreSelectionModal = false" />
    </BaseModal>
    <BaseModal class="small" v-model="showDepartmentsModal">
      <BaseDepartmentSelector @selected="showDepartmentsModal = false" />
    </BaseModal>
    <ChatQueuePanel :isOpen="showChatPanel" @close="showChatPanel = false" />
    <div class="right-menu">
      <div class="user-icon hidden">
        <img src="@/assets/icons/user.svg" class="logo" alt="user" />
      </div>
      <div
        class="notification-bell"
        :class="{ 'has-unread': totalNotifications > 0 }"
        @click="openChatPanel"
      >
        <i class="mdi mdi-bell" />
        <span v-if="totalNotifications > 0" class="notification-badge">
          {{ totalNotifications > 99 ? "99+" : totalNotifications }}
        </span>
      </div>
      <button
        class="mobile-menu-btn md:hidden"
        type="button"
        :aria-expanded="showMobileMenu ? 'true' : 'false'"
        aria-label="menu"
        @click="showMobileMenu = !showMobileMenu"
      >
        <i
          class="mdi"
          :class="showMobileMenu ? 'mdi-close' : 'mdi-dots-vertical'"
        />
      </button>
      <div
        class="collapsible-menu"
        :class="{ open: showMobileMenu }"
        @click="showMobileMenu = false"
      >
        <div class="menu-user" v-if="user">
          <span class="menu-user__avatar">{{ userInitials }}</span>
          <span class="menu-user__name">{{ user.user }}</span>
        </div>
        <div class="menu-entry menu-toggle-row" @click.stop>
          <span class="menu-entry__label">
            {{ $t("message.header.notifications") }}
          </span>
          <BasePushNotificationToggle />
        </div>
        <a
          class="menu-entry"
          @click="showStoreSelectionModal = true"
          :title="$t('message.header.store')"
        >
          <span class="menu-entry__label">
            {{ $t("message.header.store") }}
          </span>
          <span class="menu-entry__value">
            {{ activeStoreName || $t("message.header.select_store") }}
          </span>
          <i class="mdi mdi-chevron-right menu-entry__chevron" />
        </a>
        <a
          class="menu-entry"
          @click="showDepartmentsModal = true"
          :title="$t('message.header.department')"
        >
          <span class="menu-entry__label">
            {{ $t("message.header.department") }}
          </span>
          <span class="menu-entry__value">
            {{ activeDepartment || $t("message.header.select_department") }}
          </span>
          <i class="mdi mdi-chevron-right menu-entry__chevron" />
        </a>
        <div class="menu-entry menu-entry--lang" @click.stop>
          <span class="menu-entry__label">
            {{ $t("message.header.language") }}
          </span>
          <select
            v-model="currentLanguage"
            @change="changeLanguage"
            class="language-selector"
          >
            <option value="es">ES</option>
            <option value="en">EN</option>
          </select>
        </div>
        <a @click="logout" class="logout-btn">
          <i class="mdi mdi-logout logout-btn__icon" />
          <span>{{ $t("message.header.logout") }}</span>
        </a>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import ChatQueuePanel from "@/components/layoutLogged/ChatQueuePanel.vue";

export default {
  name: "LoggedHeader",
  components: { ChatQueuePanel },
  data() {
    return {
      publicPath: process.env.BASE_URL,
      sync: false,
      showStoreSelectionModal: false,
      showDepartmentsModal: false,
      showChatPanel: false,
      showMobileMenu: false,
      currentLanguage: this.$i18n.locale,
      unreadChats: 0
    };
  },
  mounted() {
    this.$store.dispatch("forms/loadUnprocessed");
    this._formsPollingInterval = setInterval(() => {
      this.$store.dispatch("forms/loadUnprocessed");
    }, 60000);
  },
  beforeDestroy() {
    if (this._formsPollingInterval) {
      clearInterval(this._formsPollingInterval);
    }
    this.removeMobileMenuListeners();
  },
  computed: {
    ...mapGetters("general", ["getAdminSettings", "getActiveStore"]),
    adminColor() {
      if (!this.getAdminSettings.get("main_menu_colors")) return "black";

      return this.getAdminSettings.get("main_menu_colors").value || "black";
    },
    appVersion() {
      return this.$store.getters["general/getAppVersion"];
    },
    user() {
      return this.$store.getters["auth/currentUser"];
    },
    userInitials() {
      const name = this.user?.user || "";
      return (
        name
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map(part => part.charAt(0).toUpperCase())
          .join("") || "?"
      );
    },
    activeStoreName() {
      return this.$store.getters["general/getActiveStore"]?.name;
    },
    activeDepartment() {
      return this.$store.getters["general/getActiveDepartment"]?.name;
    },
    activeDepartmentDebug() {
      return this.$store.getters["general/getActiveDepartment"];
    },
    queueCount() {
      return this.$store.state.chat.queue.length;
    },
    unprocessedFormsCount() {
      return this.$store.getters["forms/unprocessedCount"];
    },
    totalNotifications() {
      return this.unreadChats + this.unprocessedFormsCount;
    }
  },
  watch: {
    $route() {
      this.showMobileMenu = false;
    },
    showMobileMenu(open) {
      if (open) {
        document.addEventListener("click", this.handleOutsideClick, true);
        window.addEventListener("scroll", this.closeMobileMenu, true);
      } else {
        this.removeMobileMenuListeners();
      }
    },
    queueCount(newVal, oldVal) {
      if (newVal > oldVal) {
        this.unreadChats += newVal - oldVal;
        try {
          const audio = new Audio(
            require("@/assets/music/freesound_community-vibrating-message-37619.mp3")
          );
          audio.play();
        } catch (e) {
          // ignore audio errors
        }
      }
    }
  },
  methods: {
    closeMobileMenu() {
      this.showMobileMenu = false;
    },
    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.showMobileMenu = false;
      }
    },
    removeMobileMenuListeners() {
      document.removeEventListener("click", this.handleOutsideClick, true);
      window.removeEventListener("scroll", this.closeMobileMenu, true);
    },
    logout() {
      this.$store.dispatch("auth/logOut");
      this.$router.push("/");
    },
    openLabelPrinter() {
      if (this.getActiveStore?.endpoint) {
        const department = this.$store.getters["general/getActiveDepartment"]
          ?.code;
        const query = department ? `?departmentCode=${department}` : "";
        window.open(`${this.getActiveStore.endpoint}/labels${query}`, "_blank");
      }
    },
    changeLanguage() {
      this.$i18n.locale = this.currentLanguage;
      localStorage.setItem("language", this.currentLanguage);
    },
    openChatPanel() {
      this.showMobileMenu = false;
      this.unreadChats = 0;
      this.showChatPanel = true;
      this.$store.dispatch("forms/loadUnprocessed");
    },
    goToChat() {
      this.$router
        .push({
          name: "ChatPage",
          params: {
            storeCode: this.$route.params.storeCode,
            departmentCode: this.$route.params.departmentCode
          }
        })
        .catch(() => {});
    }
  }
};
</script>
<style lang="scss">
.small {
  .modal-content {
    max-width: 600px;
  }
}
@import "@/assets/style/css/generic-variables.scss";
#header {
  width: 100%;
  height: $headerHeight;
  box-shadow: inset 0 0 5px 2px rgba(0, 0, 0, 0.37);
  background-color: $headerBackground;
  color: $headerFontColor;
  display: flex;
  flex-direction: row;
  align-content: flex-end;
  justify-content: space-between;
  position: relative;
  z-index: 10;
  // #header crea contexto de apilamiento, así que el z-index del panel de chat
  // y del menú mobile queda confinado a este valor: hay que elevarlo mientras
  // alguno esté abierto para que no lo tape el botón de menú del sidebar.
  &.has-overlay-open {
    z-index: 60;
  }
  .left-menu-header {
    display: flex;
    flex-direction: row;
    height: 100%;
    .label-printer-btn,
    a {
      text-align: center;
      display: flex;
      padding: 0 15px;
      justify-content: center;
      align-items: center;
      color: rgb(255 255 255 / 86%);
      background-color: #ffeaea70;
      font-weight: 700;
      font-size: 12px;
      -webkit-box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.35);
      box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.35);
      cursor: pointer;
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
  }
  .app-version {
    position: absolute;
    right: 0;
    top: 0;
    font-size: 10px;
    opacity: 0.6;
    pointer-events: none;
  }
  .right-menu {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
    .collapsible-menu {
      display: flex;
      flex-direction: row;
      align-items: center;
      height: 100%;
      .menu-user {
        display: flex;
        align-items: center;
        padding: 0 15px;
        font-weight: 500;
      }
      .menu-entry--lang,
      .menu-toggle-row {
        display: flex;
        align-items: center;
      }
      // etiquetas/iconos: solo se usan en el desplegable mobile
      .menu-user__avatar,
      .menu-entry__label,
      .menu-entry__chevron,
      .logout-btn__icon {
        display: none;
      }
    }
    button.mobile-menu-btn {
      display: none;
      padding: 0 12px;
      -webkit-appearance: none;
      appearance: none;
      background: transparent;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      outline: none;
      color: rgba(255, 255, 255, 0.9);
      cursor: pointer;
      i {
        font-size: 24px;
        color: inherit;
      }
    }
    .item,
    a {
      text-align: center;
      display: flex;
      padding: 0 15px;
      justify-content: center;
      align-items: center;
      font-weight: 500;
      .logo {
        width: 25px;
        height: 25px;
      }
      .spin {
        -webkit-animation: spin 3s linear infinite;
        -moz-animation: spin 3s linear infinite;
        animation: spin 3s linear infinite;
      }
      @-moz-keyframes spin {
        100% {
          -moz-transform: rotate(360deg);
        }
      }
      @-webkit-keyframes spin {
        100% {
          -webkit-transform: rotate(360deg);
        }
      }
      @keyframes spin {
        100% {
          -webkit-transform: rotate(360deg);
          transform: rotate(360deg);
        }
      }
    }
    .notification-bell {
      position: relative;
      cursor: pointer;
      padding: 0 15px;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 22px;
        color: rgba(255, 255, 255, 0.9);
      }

      &:hover {
        opacity: 0.8;
      }

      &.has-unread i {
        animation: bell-ring 1s ease infinite;
      }

      .notification-badge {
        position: absolute;
        top: 50%;
        right: 5px;
        transform: translateY(-80%);
        background-color: #ef4444;
        color: white;
        font-size: 10px;
        font-weight: bold;
        min-width: 18px;
        height: 18px;
        border-radius: 9px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0 4px;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }
    }

    @keyframes bell-ring {
      0%,
      100% {
        transform: rotate(0);
      }
      20% {
        transform: rotate(15deg);
      }
      40% {
        transform: rotate(-15deg);
      }
      60% {
        transform: rotate(10deg);
      }
      80% {
        transform: rotate(-10deg);
      }
    }

    .language-selector {
      margin: 0 10px;
      padding: 5px 10px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      background-color: rgba(255, 255, 255, 0.1);
      color: white;
      border-radius: 4px;
      font-weight: 600;
      cursor: pointer;
      outline: none;
      transition: all 0.3s ease;
      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
      option {
        background-color: #333;
        color: white;
      }
    }
  }

  @media (max-width: 767px) {
    .app-version {
      right: auto;
      left: 50%;
      transform: translateX(-50%);
    }
    .right-menu {
      button.mobile-menu-btn {
        display: flex;
        align-items: center;
      }
      .collapsible-menu {
        display: none;
        // el ancestro .container-custom usa overflow:hidden (App.scss),
        // por eso el desplegable va fixed y no absolute: si no, se recorta.
        position: fixed;
        top: calc(#{$headerHeight} - 6px);
        right: 8px;
        left: auto;
        width: auto;
        min-width: 250px;
        max-width: 88vw;
        flex-direction: column;
        align-items: stretch;
        height: auto;
        padding: 0;
        overflow: hidden;
        background-color: #ffffff;
        color: #111827;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
        z-index: 1000;
        &.open {
          display: flex;
        }

        .menu-user {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px;
          background-color: #f9fafb;
          border-bottom: 1px solid #eceff3;
        }
        .menu-user__avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 34px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background-color: #1f2937;
          color: #ffffff;
          font-size: 13px;
          font-weight: 700;
        }
        .menu-user__name {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .menu-entry.menu-toggle-row {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 10px 16px;
          cursor: default;
          &:hover {
            background-color: transparent;
          }
          // el toggle viene estilado para el header oscuro:
          // sobre el panel blanco hay que repintar slider y texto.
          .push-notification-toggle {
            padding: 0;
          }
          .slider {
            background-color: #d1d5db;
          }
          .toggle-label {
            color: #4b5563;
            font-size: 12px;
            font-weight: 600;
          }
          .spinner {
            border-color: rgba(0, 0, 0, 0.2);
            border-top-color: #4b5563;
          }
        }

        .menu-entry {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 2px;
          height: auto;
          padding: 10px 34px 10px 16px;
          text-align: left;
          border-bottom: 1px solid #f1f3f5;
          cursor: pointer;
          &:hover {
            background-color: #f9fafb;
          }
        }
        .menu-entry__label {
          display: block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9ca3af;
        }
        .menu-entry__value {
          font-size: 14px;
          font-weight: 600;
          color: #111827;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          max-width: 100%;
        }
        .menu-entry__chevron {
          display: block;
          position: absolute;
          top: 50%;
          right: 8px;
          transform: translateY(-50%);
          font-size: 20px;
          color: #c4c9d1;
        }

        .menu-entry--lang {
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          .language-selector {
            margin: 0;
            padding: 4px 10px;
            background-color: #ffffff;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            color: #111827;
            font-size: 13px;
            &:hover {
              background-color: #f3f4f6;
            }
            option {
              background-color: #ffffff;
              color: #111827;
            }
          }
        }

        .logout-btn {
          justify-content: flex-start;
          gap: 8px;
          height: auto;
          padding: 12px 16px;
          color: #dc2626;
          font-size: 14px;
          font-weight: 600;
          &:hover {
            background-color: #fef2f2;
          }
        }
        .logout-btn__icon {
          display: block;
          font-size: 18px;
        }
      }
    }
  }
}
</style>
